import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check } from 'lucide-react';
import { subscriptionAPI } from '../../services/api';
import { buildPlanFeatureList } from '../../utils/planFeatures';
import type { PublicPlanDefinition } from '../../types/platformOrganization';

interface LandingTheme {
  accent: string;
  accentGrad: string;
  bg0: string;
  bg1: string;
  bg2: string;
  border: string;
  textPrimary: string;
  textMuted: string;
  shadowAccent: string;
}

interface LandingPricingProps {
  theme: LandingTheme;
}

function PricingCardSection({
  children,
  delay = 0,
  style,
}: {
  children: React.ReactNode;
  delay?: number;
  style?: React.CSSProperties;
}) {
  const ref = useRef(null);
  const inView = useInView(ref as React.RefObject<Element>, { once: true, margin: '-70px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      style={style}
    >
      {children}
    </motion.div>
  );
}

export function LandingPricing({ theme: t }: LandingPricingProps) {
  const { data: plansRes, isLoading } = useQuery(['subscription-plans-public'], () =>
    subscriptionAPI.listPlans()
  );
  const plans = (['BASIC', 'PROFESSIONAL', 'PREMIUM'] as const)
    .map((tier) => (plansRes?.data?.data as PublicPlanDefinition[] | undefined)?.find((p) => p.tier === tier))
    .filter(Boolean) as PublicPlanDefinition[];

  return (
    <section id="pricing" style={{ background: t.bg1, padding: '88px 24px', position: 'relative' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <PricingCardSection style={{ textAlign: 'center', marginBottom: 56 }}>
          <span
            style={{
              color: t.accent,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: 'uppercase',
            }}
          >
            Planes y precios
          </span>
          <h2
            style={{
              color: t.textPrimary,
              fontSize: 'clamp(26px,3.8vw,40px)',
              fontWeight: 800,
              marginTop: 12,
              letterSpacing: '-0.5px',
            }}
          >
            Invierte en la modernización de tu funeraria
          </h2>
          <p
            style={{
              color: t.textMuted,
              fontSize: 16,
              marginTop: 12,
              maxWidth: 440,
              margin: '12px auto 0',
            }}
          >
            Sin contratos de permanencia. Cancela cuando quieras.
          </p>
        </PricingCardSection>
        <div className="grid md:grid-cols-3" style={{ gap: 20, alignItems: 'center' }}>
          {isLoading ? (
            <p style={{ textAlign: 'center', color: t.textMuted, fontSize: 14, gridColumn: '1 / -1' }}>
              Cargando planes…
            </p>
          ) : (
            plans.map((plan, i) => {
              const highlighted = plan.tier === 'PROFESSIONAL';
              const features = buildPlanFeatureList(plan);
              const ctaStyle: React.CSSProperties = {
                display: 'block',
                textAlign: 'center',
                textDecoration: 'none',
                padding: '13px 20px',
                borderRadius: 10,
                fontWeight: 700,
                fontSize: 14,
                background: highlighted ? t.accentGrad : 'transparent',
                color: highlighted ? '#fff' : t.textMuted,
                border: highlighted ? 'none' : `1px solid ${t.border}`,
                boxShadow: highlighted ? `0 6px 20px ${t.shadowAccent}` : 'none',
              };
              return (
                <PricingCardSection key={plan.tier} delay={i * 0.12}>
                  <motion.div
                    style={{
                      background: highlighted ? t.bg2 : t.bg0,
                      border: `1px solid ${highlighted ? `${t.accent}50` : t.border}`,
                      borderRadius: 20,
                      padding: highlighted ? '40px 28px' : '32px 28px',
                      position: 'relative',
                      boxShadow: highlighted
                        ? `0 0 70px ${t.shadowAccent}20, 0 30px 60px rgba(0,0,0,0.2)`
                        : 'none',
                      transform: highlighted ? 'scale(1.04)' : 'scale(1)',
                      transition: 'transform 0.3s',
                    }}
                  >
                    {highlighted && (
                      <div
                        style={{
                          position: 'absolute',
                          top: -13,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: t.accentGrad,
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '5px 18px',
                          borderRadius: 100,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        MÁS POPULAR
                      </div>
                    )}
                    <div style={{ color: t.textMuted, fontSize: 13, fontWeight: 600 }}>{plan.label}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, margin: '12px 0 4px' }}>
                      <span
                        style={{
                          color: highlighted ? t.accent : t.textPrimary,
                          fontSize: 40,
                          fontWeight: 800,
                        }}
                      >
                        ${plan.priceUsd}
                      </span>
                      <span style={{ color: t.textMuted, fontSize: 14 }}>/mes</span>
                    </div>
                    <div style={{ height: 1, background: t.border, margin: '16px 0 20px' }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
                      {features.map((f, j) => (
                        <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              background: highlighted ? `${t.accent}20` : t.border,
                              border: `1px solid ${highlighted ? `${t.accent}40` : t.border}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Check size={9} color={highlighted ? t.accent : t.textMuted} />
                          </div>
                          <span style={{ color: t.textMuted, fontSize: 13 }}>{f}</span>
                        </div>
                      ))}
                    </div>
                    <Link
                      to={`/registro?plan=${plan.tier}`}
                      style={ctaStyle}
                    >
                      {plan.stripeCheckoutUrl ? 'Registrarse y pagar' : 'Solicitar acceso'}
                    </Link>
                  </motion.div>
                </PricingCardSection>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
}
